import * as React from 'react'

// Breathing room kept between the element and the edges of its scroll container.
const SCROLL_GAP = 24

// Scrolls the container by the exact amount needed to bring the element fully into view, and does
// nothing while it already is. `element.scrollIntoView({ block: 'nearest' })` was tried first and
// did not move the container, so the offset is applied to the container by hand.
const scrollIntoView = (node: HTMLElement | null, container: HTMLElement | null): void => {
  if (!node || !container) {
    return
  }

  const nodeRect = node.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const below = nodeRect.bottom + SCROLL_GAP - containerRect.bottom
  const above = containerRect.top + SCROLL_GAP - nodeRect.top

  if (below > 0) {
    container.scrollTo({ top: container.scrollTop + below, behavior: 'smooth' })
  } else if (above > 0) {
    container.scrollTo({ top: container.scrollTop - above, behavior: 'smooth' })
  }
}

// Keeps the referenced element inside the scroll container with the given id, for as long as it is
// mounted: on mount, and on every later row insertion. That follow-up is what makes it work —
// content that arrives afterwards (a refetched list rendering above the element) pushes it back out
// well after mount. Does nothing when the container isn't on the page, so the element can also live
// outside it.
export const useKeepInView = <T extends HTMLElement>(containerId: string): React.RefObject<T> => {
  const ref = React.useRef<T>(null)

  React.useEffect(() => {
    const node = ref.current
    const container = document.getElementById(containerId)

    scrollIntoView(node, container)

    if (!container) {
      return undefined
    }

    // Only structural changes matter: rows being added is what shifts the element. Text updates in
    // the rows already on screen leave its position alone, so `characterData` stays unobserved.
    const observer = new MutationObserver(() => scrollIntoView(node, container))

    observer.observe(container, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [containerId])

  return ref
}
